// Kalman Claudsky - Jul 5, 2026 - new module supporting the optional, smartphone-only, user-initiated PWA install
//
// Browser-first optional PWA install support.
//
// Base Hasefer is primarily a browser-based research platform. Installation is
// offered only as an optional convenience. This module captures Chrome's
// `beforeinstallprompt` event as early as possible (so it isn't missed before
// React mounts), suppresses Chrome's automatic install mini-infobar, and lets
// the UI surface a user-initiated "Install" control ONLY when installation is
// actually available and the app isn't already running installed.
//
// Import this module for its side effect (early capture); the app menu uses
// canInstall()/subscribe()/promptInstall() to render the optional control.

let deferredPrompt = null;
let installed = false;
const subscribers = new Set();

function notify() {
  const value = canInstall();
  subscribers.forEach((cb) => {
    try {
      cb(value);
    } catch (e) {
      // a broken subscriber must not break the others
    }
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Suppress Chrome's automatic promotion; we surface install on demand only.
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    installed = true;
    deferredPrompt = null;
    notify();
  });
}

// True when the page is already running as an installed app (any installable
// display mode), so we don't offer to install what is already installed.
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  const mm = window.matchMedia;
  return (
    (mm &&
      (mm('(display-mode: standalone)').matches ||
        mm('(display-mode: minimal-ui)').matches ||
        mm('(display-mode: fullscreen)').matches)) ||
    window.navigator.standalone === true // iOS Safari
  );
}

// True only for phone-class devices (not tablets or desktops). The optional
// install offer is intentionally scoped to smartphones.
export function isSmartphone() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;

  const uaData = navigator.userAgentData;
  if (uaData && typeof uaData.mobile === 'boolean') {
    // Client Hints: `mobile` is true for phone-class devices, false for tablets/desktop.
    return uaData.mobile === true;
  }

  // Fallback for engines without userAgentData (e.g. iOS Safari): phone UA + touch.
  // The `Android.*Mobile` requirement excludes Android tablets (whose UA omits "Mobile").
  const ua = navigator.userAgent || '';
  const isPhoneUA = /Android.*Mobile|iPhone|iPod|Windows Phone|BlackBerry|BB10|Opera Mini|IEMobile/i.test(ua);
  const coarsePointer = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
  return isPhoneUA && coarsePointer;
}

// Whether an optional install control should be shown right now.
// Scoped to smartphones per product preference; tablets/desktops never see it.
export function canInstall() {
  return !!deferredPrompt && !installed && !isStandalone() && isSmartphone();
}

// Subscribe to availability changes. Returns an unsubscribe function.
export function subscribe(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

// User-initiated install. Returns the outcome ('accepted' | 'dismissed') or null.
// The captured prompt can only be used once.
export async function promptInstall() {
  if (!deferredPrompt) return null;
  const promptEvent = deferredPrompt;
  deferredPrompt = null;
  promptEvent.prompt();
  let outcome = null;
  try {
    const choice = await promptEvent.userChoice;
    outcome = choice ? choice.outcome : null;
  } catch (e) {
    outcome = null;
  }
  notify();
  return outcome;
}
